import { useCallback, useEffect, useRef, useState } from 'react';
import api from '../api/client';
import { invalidate, invalidatePortfolioForAccount } from '../api/apiCache';
import { formatCurrency } from '../lib/formatCurrency';
import { isNativePlatform } from '../lib/nativeSocialAuth';
import { clearHandoff, readHandoff } from '../lib/fundingHandoff';

// v1.0.2 §3.4: after a "Fund account" hand-off, look for the deposit when the user comes back to
// the app (web: visibilitychange; native: also @capacitor/app's appStateChange), then every minute
// for at most 10 minutes per return, and say when it arrives. When an ACH deposit first appears,
// and as pending or only once settled, isn't documented: the owner's real deposit (§3.7) measures
// it, and this copy follows.
const POLL_MS = 60 * 1000;
const POLLS_PER_RETURN = 10;

export default function FundingHandoffWatcher() {
  const [message, setMessage] = useState(null);
  const timer = useRef(null);
  const polls = useRef(0);

  const stop = () => { clearInterval(timer.current); timer.current = null; };

  const check = useCallback(async () => {
    const handoff = readHandoff();
    if (!handoff) { stop(); return; }
    try {
      const { data } = await api.get(`/alpaca/accounts/${encodeURIComponent(handoff.accountId)}/deposits?after=${encodeURIComponent(handoff.startedAt)}`);
      const deposit = (data?.deposits || []).find(d => d.kind === 'deposit');
      if (!deposit) return;
      const settled = deposit.status === 'executed';
      setMessage(`Deposit of ${formatCurrency(deposit.amount)} ${settled ? 'received' : 'pending'}.`);
      if (settled) {
        clearHandoff();
        invalidate('portfolio');
        invalidatePortfolioForAccount(handoff.accountId);
        stop();
      }
    } catch { /* try again on the next poll or return */ }
  }, []);

  const onReturn = useCallback(() => {
    if (!readHandoff()) return;
    stop();
    polls.current = 0;
    check();
    timer.current = setInterval(() => {
      polls.current += 1;
      if (polls.current > POLLS_PER_RETURN) { stop(); return; }
      check();
    }, POLL_MS);
  }, [check]);

  useEffect(() => {
    const onVisibility = () => { if (!document.hidden) onReturn(); };
    document.addEventListener('visibilitychange', onVisibility);
    let handle;
    let cancelled = false;
    if (isNativePlatform()) {
      import('@capacitor/app').then(({ App }) => {
        if (cancelled) return;
        App.addListener('appStateChange', ({ isActive }) => { if (isActive) onReturn(); })
          .then(h => { handle = h; }).catch(() => {});
      }).catch(() => {});
    }
    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', onVisibility);
      handle?.remove();
      stop();
    };
  }, [onReturn]);

  if (!message) return null;
  return (
    <div className="funding-handoff__status" role="status">
      <span>{message}</span>
      <button type="button" className="funding-handoff__dismiss" aria-label="Dismiss" onClick={() => setMessage(null)}>×</button>
    </div>
  );
}
